import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import classNames from 'classnames'
import DOMPurify from 'dompurify'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Drawer, Spinner } from 'components/ui'
import { HiOutlineCloudUpload, HiPlus } from 'react-icons/hi'
import { fetchMockups, selectMockup } from 'store/mockups/mockupsSlice'
import {
    apiGetHuduArticles,
    apiGetHuduCompanies,
    apiGetHuduFolders,
    apiPublishToHudu,
} from 'services/ArticleService'
import './KBArticle.css'

/**
 * KBArticle – smart mockup panel with toolbar and Save-to-Hudu drawer.
 *
 * Props:
 *   onStartNewMockup  {function}  Called when the user clicks the + (New Mockup) button.
 *   isMockupLoading   {bool}      True while the parent is generating content.
 *   mockupError       {string}    Error message from the parent.
 *   className         {string}    Additional CSS classes applied to the outer wrapper.
 *   style             {object}    Inline styles applied to the outer wrapper.
 */
const KBArticle = ({
    onStartNewMockup,
    isMockupLoading = false,
    mockupError = '',
    className,
    style,
}) => {
    const dispatch = useDispatch()

    // ── Redux ───────────────────────────────────────────────────────────────
    const mockups = useSelector((state) => state.mockups.items || {})
    const mockupOrder = useSelector((state) => state.mockups.order || [])
    const currentMockupId = useSelector((state) => state.mockups.currentMockupId)

    const activeMockup = currentMockupId ? mockups[currentMockupId] : null
    const rawContent = activeMockup?.draftHtml || ''
    const content = useMemo(() => rawContent ? DOMPurify.sanitize(rawContent) : '', [rawContent])

    // ── Fetch mockups on mount ──────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchMockups())
    }, [dispatch])

    // ── Save-to-Hudu drawer state ──────────────────────────────────────────
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [articleName, setArticleName] = useState('')
    const [companies, setCompanies] = useState([])
    const [companiesLoading, setCompaniesLoading] = useState(false)
    const [selectedCompanyId, setSelectedCompanyId] = useState('')
    const [folders, setFolders] = useState([])
    const [foldersLoading, setFoldersLoading] = useState(false)
    const [selectedFolderId, setSelectedFolderId] = useState('')
    const [articles, setArticles] = useState([])
    const [articlesLoading, setArticlesLoading] = useState(false)
    const [selectedArticleId, setSelectedArticleId] = useState('')
    const [publishLoading, setPublishLoading] = useState(false)
    const [publishError, setPublishError] = useState('')

    const companiesFetchedRef = useRef(false)

    const openDrawer = useCallback(() => {
        setArticleName(activeMockup?.title || '')
        setSelectedCompanyId('')
        setSelectedFolderId('')
        setSelectedArticleId('')
        setFolders([])
        setArticles([])
        setPublishError('')
        setDrawerOpen(true)

        if (!companiesFetchedRef.current) {
            setCompaniesLoading(true)
            apiGetHuduCompanies({})
                .then((res) => {
                    const list = res?.data?.data?.companies || []
                    setCompanies(list)
                    companiesFetchedRef.current = true
                })
                .catch(() => setCompanies([]))
                .finally(() => setCompaniesLoading(false))
        }
    }, [activeMockup?.title])

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false)
        setPublishError('')
    }, [])

    // Refresh folders whenever company selection or drawer-open changes
    useEffect(() => {
        if (!drawerOpen) return
        setSelectedFolderId('')
        setFolders([])
        setFoldersLoading(true)
        apiGetHuduFolders({ companyId: selectedCompanyId || undefined })
            .then((res) => {
                const list = res?.data?.data?.folders || []
                setFolders(list)
            })
            .catch(() => setFolders([]))
            .finally(() => setFoldersLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId, drawerOpen])

    useEffect(() => {
        if (!drawerOpen) return
        setArticles([])
        setArticlesLoading(true)
        apiGetHuduArticles({
            companyId: selectedCompanyId || undefined,
            folderId: selectedFolderId || undefined,
        })
            .then((res) => {
                const list = res?.data?.data?.articles || []
                setArticles(list)
                setSelectedArticleId((currentValue) =>
                    list.some((article) => String(article.id) === String(currentValue))
                        ? currentValue
                        : ''
                )
            })
            .catch(() => {
                setArticles([])
                setSelectedArticleId('')
            })
            .finally(() => setArticlesLoading(false))
    }, [drawerOpen, selectedCompanyId, selectedFolderId])

    const handlePublish = useCallback(async () => {
        if (!articleName.trim() || !content) {
            setPublishError('Article name and content are required.')
            return
        }
        setPublishLoading(true)
        setPublishError('')
        try {
            const res = await apiPublishToHudu({
                name: articleName.trim(),
                content,
                companyId: selectedCompanyId ? parseInt(selectedCompanyId, 10) : undefined,
                folderId: selectedFolderId ? parseInt(selectedFolderId, 10) : undefined,
                articleId: selectedArticleId
                    ? parseInt(selectedArticleId, 10)
                    : undefined,
                mockupId: activeMockup?.id,
                sourceMetadata: activeMockup?.metadata || undefined,
            })
            const url = res?.data?.data?.url
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer')
            }
            closeDrawer()
        } catch (err) {
            setPublishError(
                err?.response?.data?.message || 'Failed to publish article to Hudu.'
            )
        } finally {
            setPublishLoading(false)
        }
    }, [
        articleName,
        closeDrawer,
        content,
        selectedArticleId,
        selectedCompanyId,
        selectedFolderId,
        activeMockup?.id,
        activeMockup?.metadata,
    ])

    // ── Drawer footer ────────────────────────────────────────────────────────
    const drawerFooter = (
        <div className="flex w-full items-center justify-end gap-2">
            <Button size="sm" onClick={closeDrawer} disabled={publishLoading}>
                Cancel
            </Button>
            <Button
                size="sm"
                variant="solid"
                onClick={handlePublish}
                loading={publishLoading}
                disabled={!articleName.trim() || !content || publishLoading}
            >
                {selectedArticleId ? 'Overwrite in Hudu' : 'Save to Hudu'}
            </Button>
        </div>
    )

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            className={classNames('kb-article-panel flex flex-col h-full min-h-0', className)}
            style={style}
        >
            {/* ── Toolbar: Dropdown | + | Save ── */}
            <div className="flex-shrink-0 flex items-center gap-2 mb-2">
                {mockupOrder.length > 0 ? (
                    <select
                        className="input input-sm flex-1 min-w-0"
                        value={currentMockupId || ''}
                        onChange={(e) => dispatch(selectMockup(e.target.value || null))}
                        disabled={isMockupLoading}
                    >
                        <option value="">Select KB Mockup</option>
                        {mockupOrder.map((id) => {
                            const m = mockups[id]
                            return (
                                <option key={id} value={id}>
                                    {m?.title || `KB Mockup ${id.slice(0, 6)}`}
                                </option>
                            )
                        })}
                    </select>
                ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500 italic flex-1">
                        No mockups yet
                    </span>
                )}

                <Button
                    size="sm"
                    shape="circle"
                    variant="twoTone"
                    icon={<HiPlus />}
                    title="Start New KB Mockup"
                    loading={isMockupLoading}
                    disabled={isMockupLoading}
                    onClick={onStartNewMockup}
                />

                <Button
                    size="sm"
                    shape="circle"
                    variant="twoTone"
                    icon={<HiOutlineCloudUpload />}
                    title="Save to Hudu"
                    disabled={!content || isMockupLoading}
                    onClick={openDrawer}
                />
            </div>

            {/* ── Parent-level error ── */}
            {mockupError ? (
                <p className="text-sm text-red-500 flex-shrink-0 mb-1">{mockupError}</p>
            ) : null}

            {/* ── Article content ── */}
            {isMockupLoading ? (
                <div className="flex flex-1 items-center justify-center">
                    <Spinner size={32} />
                </div>
            ) : content ? (
                <div
                    className="kb-article prose prose-sm max-w-none dark:prose-invert flex-initial overflow-auto min-h-full"
                    // Content originates from our own AI pipeline — not user-supplied arbitrary HTML.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            ) : (
                <div className="kb-article flex flex-1 items-center justify-center">
                    <p className="text-gray-400 dark:text-gray-500 text-sm italic">
                        No content to display. Start a new KB mockup with the{' '}
                        <HiPlus className="inline" /> button.
                    </p>
                </div>
            )}

            {/* ── Save-to-Hudu Drawer ── */}
            <Drawer
                title="Save to Hudu"
                isOpen={drawerOpen}
                onClose={closeDrawer}
                onRequestClose={closeDrawer}
                placement="right"
                width={420}
                footer={drawerFooter}
            >
                <div className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Article Name
                        </label>
                        <input
                            className="input w-full"
                            type="text"
                            value={articleName}
                            onChange={(e) => setArticleName(e.target.value)}
                            placeholder="Enter article name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Company{' '}
                            <span className="text-gray-400 font-normal">
                                (or leave blank for global KB)
                            </span>
                        </label>
                        {companiesLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Spinner size={16} /> Loading companies…
                            </div>
                        ) : (
                            <select
                                className="input w-full"
                                value={selectedCompanyId}
                                onChange={(e) => {
                                    setSelectedCompanyId(e.target.value)
                                    setSelectedArticleId('')
                                }}
                            >
                                <option value="">— Global KB —</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Folder{' '}
                            <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        {foldersLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Spinner size={16} /> Loading folders…
                            </div>
                        ) : (
                            <select
                                className="input w-full"
                                value={selectedFolderId}
                                onChange={(e) => {
                                    setSelectedFolderId(e.target.value)
                                    setSelectedArticleId('')
                                }}
                            >
                                <option value="">— No folder —</option>
                                {folders.map((f) => (
                                    <option key={f.id} value={f.id}>
                                        {f.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {!foldersLoading && folders.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1">
                                {selectedCompanyId
                                    ? 'No folders found for this company.'
                                    : 'No global folders found.'}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Existing Article{' '}
                            <span className="text-gray-400 font-normal">
                                (optional overwrite target)
                            </span>
                        </label>
                        {articlesLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Spinner size={16} /> Loading articles…
                            </div>
                        ) : (
                            <select
                                className="input w-full"
                                value={selectedArticleId}
                                onChange={(e) => {
                                    const nextValue = e.target.value
                                    setSelectedArticleId(nextValue)
                                    if (!nextValue) {
                                        setArticleName(activeMockup?.title || '')
                                        return
                                    }

                                    const selectedArticle = articles.find(
                                        (article) =>
                                            String(article.id) === String(nextValue)
                                    )
                                    if (selectedArticle?.name) {
                                        setArticleName(selectedArticle.name)
                                    }
                                }}
                            >
                                <option value="">— Create new article —</option>
                                {articles.map((article) => (
                                    <option key={article.id} value={article.id}>
                                        {article.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {!articlesLoading && articles.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1">
                                {selectedCompanyId
                                    ? 'No existing company articles found for this filter.'
                                    : 'No existing global articles found for this filter.'}
                            </p>
                        )}
                        {selectedArticleId ? (
                            <p className="text-xs text-amber-500 mt-1">
                                Saving will update the selected Hudu article instead of creating a new one.
                            </p>
                        ) : null}
                    </div>

                    {publishError ? (
                        <p className="text-sm text-red-500">{publishError}</p>
                    ) : null}
                </div>
            </Drawer>
        </div>
    )
}

export default KBArticle
